import { defineComponent as _defineComponent } from 'vue'
import HeaderLayout from '@/components/header/Header.vue';
import SidebarLayout from '@/components/SidebarLayout.vue';
import Spinner from '@/components/Spinner.vue';
import TermsAgreementModal from '@/components/TermsAgreementModal.vue';
import { mastersService } from './services/masters.service';
import { devicesService } from './services/devices.service';
import { usersService } from './services/users.service';
import { notificationsService } from './services/notifications.service';
import { User } from './models/user';
import { tenantsService } from './services/tenants.service';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import { userTargetsService } from './services/userTargets.service';
import { careAlertsService } from './services/careAlerts.service';
import { Routes } from './router/routes';
import { shiftsService } from '@/services/shifts.service';
import { getAllDevicesByCustomerId } from '@/use-cases/devices';
import {
  canAccessWorkCenterConsole,
  hasCurrentUserPermission,
} from './utils/permissionUtils';
import { UserType } from './models/userType';
import { useRootStore } from './store';
import { computed, getCurrentInstance, onBeforeMount, ref, watch } from 'vue';
import { modalService } from './library-services/modal.service';
import { useRoute } from '@/composables/router';
import {
  useAuth,
  useSignalR,
  useSpinner,
  useAutoRemovedResizeListener,
} from '@/composables/misc';
import { locationsService } from './services/locations.service';
import { workCenterAlertsService } from '@/services/workCenterAlerts.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const spinner = useSpinner();
const auth = useAuth();
const signalr = useSignalR();

const sidebarRef = ref<InstanceType<typeof SidebarLayout>>();
const showSidebar = ref(window.innerWidth > 1024);
const previousWidth = ref<number>();

onBeforeMount(() => {
  if (useRoute().value.name !== Routes.LoginCallback) {
    auth.isLoggedIn().then((loggedIn) => {
      if (!loggedIn) {
        auth.login();
      } else {
        getUserInfo();
      }
    });
  }
  useAutoRemovedResizeListener(adjustSidebarOnResize);
});

/**
 * Fetches and saves into Vuex store several useful information used throughout
 * the application.
 *
 * The Vuex store is persisted in the browser local store, so it's always
 * available after the first load. That's why the get*() methods that fetch
 * the data aren't awaited. The first time a user logs in in the page doesn't
 * finish loading until SetUserInfoLoaded is dispatched (see loadedInfoUser
 * check in Index template). The rest of initial pages (Care and Technology
 * consoles) don't need these data.
 */
async function getUserInfo() {
  spinner.showSpinner();

  try {
    const user = await usersService.me();
    // User is updated after retrieval to store a potentially modified
    // dashboard, modification done to support retrocompatibility.
    // Only applies to users who have a dashboard
    if (user.isCustomer) {
      await usersService.updateDashboards();
    }
    signalr.signIn();
    if (hasCurrentUserPermission(UserType.CustomerUser)) {
      getTargets();
    }

    if (user.hasCustomer) {
      await getCustomer(user.customerId!);
      await Promise.all([
        getDevices(user),
        getLocations(user),
        getShifts(user.customerId!),
      ]);
      getUserTargets(user.customerId!);
    } else {
      await tenantsService.store.update(null);
      devicesService.store.updateAll([]);
    }
    if (
      hasCurrentUserPermission(UserType.SubsidiaryAdmin) ||
      (hasCurrentUserPermission(UserType.CustomerAdmin) &&
        canAccessWorkCenterConsole())
    ) {
      getWccAlertKpis();
    } else if (hasCurrentUserPermission(UserType.ServiceUser)) {
      getCareAlertKpis();
    }
    if (user.isServiceUserType || user.isTechnologyUser) {
      getEventModules();
      getEventCodes();
    }

    getTimezones();
    getCurrencyCodes();
    getMeasurements();
    getDeviceBaseModels();
    getNotifications();
  } finally {
    useRootStore().setUserInfoLoaded(true);
    spinner.removeSpinner();
  }
}

async function getCustomer(customerId: number) {
  spinner.showSpinner();
  const customer = await tenantsService
    .getById(customerId)
    .finally(() => spinner.removeSpinner());
  if (customer.hasExpired()) {
    await auth.logout();
  }
  await tenantsService.store.update(customer);
}

async function getDevices(user: User) {
  if (!user.consoles.includes(ConsoleEnum.WCC)) {
    return;
  }
  spinner.showSpinner();
  await getAllDevicesByCustomerId(user.customerId!);
  spinner.removeSpinner();
}

async function getLocations(user: User) {
  if (!user.consoles.includes(ConsoleEnum.WCC)) {
    return;
  }
  spinner.showSpinner();
  await locationsService.get(user.customerId!);
  spinner.removeSpinner();
}

async function getShifts(customerId: number) {
  spinner.showSpinner();
  // getByTenantId already adds the shifts to the store, so we don't add them here
  await shiftsService
    .getByTenantId(customerId)
    .finally(() => spinner.removeSpinner());
}

async function getTargets() {
  spinner.showSpinner();
  await userTargetsService.getTargets().finally(() => spinner.removeSpinner());
}

async function getUserTargets(tenantId: number) {
  spinner.showSpinner();
  await userTargetsService
    .getForTenant(tenantId)
    .finally(() => spinner.removeSpinner());
}

async function getTimezones() {
  spinner.showSpinner();
  await mastersService.getTimezones().finally(() => spinner.removeSpinner());
}

async function getCurrencyCodes() {
  spinner.showSpinner();
  await mastersService
    .getCurrencyCodes()
    .finally(() => spinner.removeSpinner());
}

async function getMeasurements() {
  spinner.showSpinner();
  await mastersService
    .getMeasurementUnits()
    .finally(() => spinner.removeSpinner());
}

async function getDeviceBaseModels() {
  spinner.showSpinner();
  await devicesService
    .getAllModels()
    .finally(() => spinner.removeSpinner());
}

async function getNotifications() {
  spinner.showSpinner();
  await notificationsService.get().finally(() => spinner.removeSpinner());
}

async function getWccAlertKpis() {
  spinner.showSpinner();
  await workCenterAlertsService
    .getAllKpis()
    .finally(() => spinner.removeSpinner());
}

async function getCareAlertKpis() {
  spinner.showSpinner();
  await careAlertsService.getAllKpis().finally(() => spinner.removeSpinner());
}

async function getEventModules() {
  spinner.showSpinner();
  await mastersService.getEventModules().finally(() => spinner.removeSpinner());
}

async function getEventCodes() {
  spinner.showSpinner();
  await mastersService.getEventCodes().finally(() => spinner.removeSpinner());
}

function adjustSidebarOnResize() {
  const width = window.innerWidth;
  if (width !== previousWidth.value) {
    if (width >= 1024 !== showSidebar.value) {
      previousWidth.value = width;
      showSidebar.value = width >= 1024;
    }
  }
}

function toggleSidebar() {
  sidebarRef.value?.toggle();
}

async function toggleBeta() {
  await usersService.toggleBeta();
}

const loadedInfoUser = computed<boolean>(() => useRootStore().userInfoLoaded);

const app = getCurrentInstance();
watch(loadedInfoUser, () => {
  if (loadedInfoUser.value && showTermsAgreement.value) {
    modalService.compositionApi.open(
      {
        component: TermsAgreementModal,
        canCancel: false,
      },
      app,
    );
  }
});

const user = computed<User>(() => usersService.store.current());

const showTermsAgreement = computed(() => {
  const persistent = user.value.isAcceptedTermsOfAgreement;
  const session = useRootStore().termsAcceptedSession;

  return user.value.hasCustomer && !persistent && !session;
});

return { __sfc: true,spinner, auth, signalr, sidebarRef, showSidebar, previousWidth, getUserInfo, getCustomer, getDevices, getLocations, getShifts, getTargets, getUserTargets, getTimezones, getCurrencyCodes, getMeasurements, getDeviceBaseModels, getNotifications, getWccAlertKpis, getCareAlertKpis, getEventModules, getEventCodes, adjustSidebarOnResize, toggleSidebar, toggleBeta, loadedInfoUser, app, user, showTermsAgreement, HeaderLayout, SidebarLayout, Spinner }
}

})